import React, { cloneElement, isValidElement, useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { Button } from '@actual-app/components/button';
import { Input } from '@actual-app/components/input';
import { styles } from '@actual-app/components/styles';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { addDays } from '@actual-app/core/shared/mtxbudget';
import type {
  WalkCharge,
  WalkStep,
} from '@actual-app/core/shared/mtxbudget-parcours';

import { useNavigate } from '#hooks/useNavigate';

import {
  Line,
  longDate,
  MtxPage,
  Section,
  shortDate,
  textDate,
  useMoney,
} from './ui';
import { useMtxbudget } from './useMtxbudget';
import { useParcours } from './useParcours';

const FORTNIGHTS_PER_YEAR = 26;

function hours(value: number) {
  return `${value.toLocaleString('fr-CA', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} h`;
}

function parseHours(value: string): number | null {
  const parsed = Number(value.replace(',', '.'));
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 168
    ? parsed
    : null;
}

function percent(balance: number, limit: number) {
  return `${Math.round((balance / limit) * 100)} %`;
}

/** The on/off switch of a payment line. */
function PaySwitch({
  label,
  isOn,
  onChange,
}: {
  label: string;
  isOn: boolean;
  onChange: (isOn: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={isOn}
      aria-label={label}
      onClick={() => onChange(!isOn)}
      style={{
        position: 'relative',
        flexShrink: 0,
        width: 44,
        height: 26,
        padding: 0,
        borderRadius: 13,
        border: isOn ? 0 : `1px solid ${theme.tableBorder}`,
        backgroundColor: isOn
          ? theme.buttonPrimaryBackground
          : theme.tableHeaderBackground,
        cursor: 'pointer',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: isOn ? 3 : 2,
          left: isOn ? 21 : 2,
          width: 20,
          height: 20,
          borderRadius: 10,
          backgroundColor: isOn
            ? theme.buttonPrimaryText
            : theme.pageTextSubdued,
        }}
      />
    </button>
  );
}

/** One line of the lists of this screen, with room for a third line and a switch. */
function Row({
  lead,
  title,
  detail,
  detailColor,
  note,
  value,
  muted = false,
  struck = false,
  action,
  last = false,
}: {
  lead?: string;
  title: string;
  detail?: string;
  detailColor?: string;
  note?: string;
  value: string;
  muted?: boolean;
  struck?: boolean;
  action?: ReactNode;
  last?: boolean;
}) {
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        padding: '10px 16px',
        borderBottom: last ? undefined : `1px solid ${theme.tableBorder}`,
      }}
    >
      {lead != null && (
        <Text
          style={{
            width: 48,
            flexShrink: 0,
            fontSize: 12,
            fontWeight: 600,
            color: theme.pageTextSubdued,
          }}
        >
          {lead}
        </Text>
      )}
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={{
            fontWeight: 500,
            color: muted ? theme.pageTextSubdued : undefined,
          }}
        >
          {title}
        </Text>
        {detail != null && (
          <Text
            style={{
              fontSize: 12,
              color: detailColor ?? theme.pageTextSubdued,
            }}
          >
            {detail}
          </Text>
        )}
        {note != null && (
          <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
            {note}
          </Text>
        )}
      </View>
      <Text
        style={{
          flexShrink: 0,
          fontWeight: 500,
          color: muted ? theme.pageTextSubdued : undefined,
          textDecoration: struck ? 'line-through' : undefined,
          ...styles.tnum,
        }}
      >
        {value}
      </Text>
      {action}
    </View>
  );
}

export function ParcoursPage() {
  const { t } = useTranslation();
  const money = useMoney();
  const navigate = useNavigate();
  const { config, saveConfig, isLoading, plan } = useMtxbudget();
  const { status, steps, checks } = useParcours();
  // Opened on the pay chosen in the list of weeks, the first one otherwise.
  const [searchParams] = useSearchParams();
  const chosen = steps.findIndex(s => s.payday === searchParams.get('paie'));
  const [index, setIndex] = useState<number | null>(null);
  const current = index ?? Math.max(0, chosen);

  const step: WalkStep | undefined = steps[Math.min(current, steps.length - 1)];

  if (!config || !step) {
    return (
      <MtxPage title={t('Parcours des quinzaines')} back="/quinzaine/parcours">
        <Section style={{ padding: 16 }}>
          <Text
            style={{
              color:
                status === 'failed' ? theme.errorText : theme.pageTextSubdued,
            }}
          >
            {status === 'failed'
              ? t('Le calcul a échoué : {{detail}}', {
                  detail: checks[0]?.detail ?? '',
                })
              : !isLoading && !config
                ? t("MTXbudget n'est pas encore configuré.")
                : t('Calcul en cours…')}
          </Text>
        </Section>
      </MtxPage>
    );
  }

  const position = Math.min(current, steps.length - 1);
  // The hours come from the same plan as Ma quinzaine and the list of weeks:
  // one figure everywhere. The walk shares out the money, not the hours.
  const fortnight = plan.find(f => f.payday === step.payday);
  const threshold =
    fortnight?.thresholdHoursPerWeek ?? step.thresholdHoursPerWeek;
  const shortfall = fortnight
    ? fortnight.charges.reduce(
        (sum, c) =>
          sum + (c.coverage.kind === 'short' ? c.coverage.missing : 0),
        0,
      )
    : step.shortfall;
  const last = steps[steps.length - 1];
  const spent =
    step.debts.reduce((sum, d) => sum + d.extra, 0) +
    step.needs.reduce((sum, n) => sum + n.paid, 0);
  const planned = (step.hours[0] + step.hours[1]) / 2;
  const entered = config.pays[step.payday]?.hours !== undefined;
  // Its hours are done: more of them would only come with a later pay.
  const worked = step.status === 'worked' || step.status === 'received';
  const passed = checks.filter(check => check.ok).length;

  function setHours(week: 0 | 1, value: string) {
    const parsed = parseHours(value);
    if (parsed === null || !config || !step) {
      return;
    }
    const next: [number, number] = [...step.hours];
    next[week] = parsed;
    saveConfig({
      ...config,
      pays: {
        ...config.pays,
        [step.payday]: { ...config.pays[step.payday], hours: next },
      },
    });
  }

  function setPaid(charge: WalkCharge, isPaid: boolean) {
    if (!config) {
      return;
    }
    const skipped = { ...config.skipped };
    if (isPaid) {
      delete skipped[charge.id];
    } else {
      skipped[charge.id] = true;
    }
    saveConfig({ ...config, skipped });
  }

  function skippedNote(charge: WalkCharge) {
    const debt = step?.debts.find(d => d.id === charge.debtId);
    if (!debt) {
      return t(
        '{{amount}} récupérés et répartis ailleurs. Réactivez la ligne pour le payer.',
        { amount: money(-charge.amount) },
      );
    }
    return t(
      "{{amount}} récupérés et répartis ailleurs. Le solde ne baisse pas : + {{interest}} d'intérêts d'ici la paie suivante, et un retard peut être signalé à l'agence de crédit.",
      {
        amount: money(-charge.amount),
        interest: money(
          Math.round((-charge.amount * debt.rate) / 100 / FORTNIGHTS_PER_YEAR),
        ),
      },
    );
  }

  const served = step.debts.filter(d => d.extra > 0);
  const rest: ReactNode[] = [
    <Row
      key="cushion"
      title={t('Coussin gardé dans le compte')}
      detail={t('solde du compte après cette paie')}
      value={money(step.cashOut - step.setAside)}
    />,
    step.setAsideFor ? (
      <Row
        key="aside"
        title={t('Mise de côté pour la paie du {{date}}', {
          date: textDate(step.setAsideFor),
        })}
        detail={t('Elle ne suffit pas seule')}
        detailColor={theme.warningText}
        note={t("reste dans le compte jusqu'au {{date}}", {
          date: textDate(step.setAsideFor),
        })}
        value={money(step.setAside)}
      />
    ) : (
      <Row
        key="aside"
        title={t('Mise de côté pour une autre paie')}
        detail={t("aucune paie suivante n'en a besoin")}
        value={money(0)}
      />
    ),
    ...served.map(debt => (
      <Row
        key={debt.id}
        title={debt.name}
        detail={t('Dette à diminuer · versement le {{date}}', {
          date: textDate(step.payday),
        })}
        detailColor={theme.noticeTextLight}
        note={
          debt.limit
            ? t(
                'solde {{balance}} · {{after}} de la limite (était {{before}})',
                {
                  balance: money(debt.balanceAfter),
                  after: percent(debt.balanceAfter, debt.limit),
                  before: percent(debt.balanceBefore, debt.limit),
                },
              )
            : t('solde {{balance}}', { balance: money(debt.balanceAfter) })
        }
        value={money(debt.extra)}
      />
    )),
    ...step.needs.map(need => (
      <Row
        key={need.id}
        title={need.name}
        detail={t('Besoin daté · {{amount}} pour le {{date}}', {
          amount: money(need.amount),
          date: textDate(need.wished),
        })}
        detailColor={theme.warningText}
        note={
          need.paid > 0
            ? t('{{paid}} réunis sur {{amount}}', {
                paid: money(need.paidSoFar),
                amount: money(need.amount),
              })
            : need.paidSoFar >= need.amount
              ? t('déjà financé')
              : step.payday > need.wished
                ? t('sa date est passée : {{missing}} ont manqué', {
                    missing: money(need.amount - need.paidSoFar),
                  })
                : t('rien sur cette paie : les dettes passent avant')
        }
        value={money(need.paid)}
      />
    )),
  ];

  return (
    <MtxPage title={t('Parcours des quinzaines')} back="/quinzaine/parcours">
      <View style={{ gap: 12, paddingBottom: 20 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Button
            isDisabled={position === 0}
            onPress={() => setIndex(position - 1)}
            style={{ flex: 1, minHeight: 48 }}
          >
            <Trans>‹ Précédente</Trans>
          </Button>
          <View style={{ alignItems: 'center', padding: '0 4px' }}>
            <Text style={{ fontWeight: 600, color: theme.pageTextDark }}>
              {t('Paie {{position}} sur {{count}}', {
                position: position + 1,
                count: steps.length,
              })}
            </Text>
            <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
              {t("jusqu'au {{date}}", { date: textDate(last.nextPayday) })}
            </Text>
          </View>
          <Button
            isDisabled={position === steps.length - 1}
            onPress={() => setIndex(position + 1)}
            style={{ flex: 1, minHeight: 48 }}
          >
            <Trans>Suivante ›</Trans>
          </Button>
        </View>

        <Section style={{ padding: 16, gap: 12 }}>
          <Text
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: theme.pageTextSubdued,
            }}
          >
            {t(
              'Paie du {{payday}} · couvre les charges du {{start}} au {{end}}',
              {
                payday: longDate(step.payday),
                start: textDate(step.payday),
                end: textDate(addDays(step.nextPayday, -1)),
              },
            )}
          </Text>
          <View
            style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              alignItems: 'baseline',
              columnGap: 8,
            }}
          >
            <Text
              style={{
                whiteSpace: 'nowrap',
                fontSize: 64,
                fontWeight: 600,
                lineHeight: 1,
                color: theme.pageTextDark,
                ...styles.tnum,
              }}
            >
              {hours(
                worked
                  ? planned
                  : Math.max(threshold, config.pay.minHoursPerWeek),
              )}
            </Text>
            <Text
              style={{
                fontSize: 15,
                fontWeight: 500,
                color: theme.pageTextSubdued,
              }}
            >
              {t('par semaine')}
              <br />
              {worked
                ? t('déjà travaillées du {{start}} au {{end}}', {
                    start: textDate(step.periodStart),
                    end: textDate(step.periodEnd),
                  })
                : threshold > config.pay.minHoursPerWeek
                  ? t('{{hours}} de plus que votre base', {
                      hours: hours(threshold - config.pay.minHoursPerWeek),
                    })
                  : t('votre base suffit')}
            </Text>
          </View>
          <View
            style={{
              gap: shortfall > 0 && worked ? 8 : 2,
              padding: '10px 12px',
              borderRadius: 4,
              border: `1px solid ${shortfall > 0 && worked ? theme.errorBorder : theme.tableBorder}`,
              backgroundColor:
                shortfall > 0 && worked
                  ? theme.errorBackground
                  : theme.tableHeaderBackground,
            }}
          >
            {shortfall > 0 ? (
              <>
                <Text style={{ fontWeight: 600, color: theme.errorText }}>
                  {t('Cette paie ne suffit pas : il manque {{amount}}', {
                    amount: money(shortfall),
                  })}
                </Text>
                <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
                  {worked
                    ? t(
                        "Ses heures sont déjà travaillées. Des heures en plus n'arriveraient qu'avec la paie du {{next}}, après les charges qui manquent.",
                        { next: textDate(step.nextPayday) },
                      )
                    : t(
                        'Aux heures prévues ({{planned}}), et aucune paie avant elle pour mettre de côté. Il faudrait {{more}} de plus par semaine.',
                        {
                          planned: hours(planned),
                          more: hours(Math.max(0, threshold - planned)),
                        },
                      )}
                </Text>
                {worked && (
                  <Button
                    variant="primary"
                    onPress={() => navigate('/quinzaine/optimiser')}
                    style={{ minHeight: 44, fontWeight: 600 }}
                  >
                    <Trans>Voir les solutions</Trans>
                  </Button>
                )}
              </>
            ) : (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <Text style={{ fontWeight: 600, color: theme.noticeTextLight }}>
                  <Trans>Cette paie suffit</Trans>
                </Text>
                <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
                  {spent > 0
                    ? t('surplus de {{amount}}', { amount: money(spent) })
                    : t('aucun surplus')}
                </Text>
              </View>
            )}
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <View style={{ flex: 1 }}>
              <Text style={{ fontWeight: 500 }}>
                <Trans>Heures prévues</Trans>
              </Text>
              <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
                {step.netIsEstimate
                  ? entered
                    ? t('Heures saisies. Changez-les : tout se recalcule.')
                    : t('Heures garanties. Montez-les : tout se recalcule.')
                  : t('Paie déjà reçue.')}
              </Text>
            </View>
            {([0, 1] as const).map(week => (
              <Input
                key={`${step.payday}-${week}-${step.hours[week]}`}
                aria-label={t('Heures de la semaine {{week}}', {
                  week: week + 1,
                })}
                inputMode="decimal"
                defaultValue={String(step.hours[week]).replace('.', ',')}
                onUpdate={value => setHours(week, value)}
                style={{ width: 64, minHeight: 44, textAlign: 'right' }}
              />
            ))}
          </View>
        </Section>

        <Section
          title={t('Ce qui entre')}
          aside={money(
            step.cashIn - step.refund + step.net + step.totalIncomes,
          )}
        >
          <Row
            title={t('Salaire net')}
            detail={
              step.netIsEstimate
                ? t(
                    'brut {{gross}} − impôt {{tax}} − retenues {{deductions}}',
                    {
                      gross: money(step.gross),
                      tax: money(step.tax),
                      deductions: money(step.deductions),
                    },
                  )
                : t('montant reçu')
            }
            value={money(step.net)}
          />
          <Row
            title={
              position === 0
                ? t("Disponible aujourd'hui")
                : t('Reporté de la paie du {{date}}', {
                    date: textDate(steps[position - 1].payday),
                  })
            }
            detail={
              position === 0
                ? t('le solde de vos comptes courants')
                : step.refund > 0
                  ? t('le manque de cette paie, à combler en premier')
                  : steps[position - 1].setAside > 0
                    ? t('le coussin et la mise de côté')
                    : t('le coussin gardé dans le compte')
            }
            value={money(step.cashIn - step.refund)}
            detailColor={step.refund > 0 ? theme.warningText : undefined}
          />
          {step.incomes.map(income => (
            <Row
              key={income.id}
              title={income.name}
              detail={t("Entrée d'argent · disponible le {{date}}", {
                date: shortDate(income.date),
              })}
              detailColor={theme.noticeTextLight}
              value={money(income.amount)}
            />
          ))}
          <View style={{ padding: '12px 16px' }}>
            <Button
              onPress={() =>
                navigate(`/previsions?ajouter=entree&date=${step.payday}`)
              }
              style={{ minHeight: 44 }}
            >
              <Trans>Ajouter une entrée d'argent</Trans>
            </Button>
          </View>
        </Section>

        <Section
          title={t('Charges à payer avec cette paie')}
          aside={money(step.totalCharges)}
        >
          {step.charges.length === 0 && (
            <Text style={{ padding: 16, color: theme.pageTextSubdued }}>
              <Trans>Aucune charge avant la paie suivante.</Trans>
            </Text>
          )}
          {step.charges.map(charge => (
            <Row
              key={charge.id}
              lead={shortDate(charge.date)}
              title={charge.name}
              detail={
                charge.skipped
                  ? t('Désactivé : ne sera pas payé le {{date}}', {
                      date: textDate(charge.date),
                    })
                  : charge.cardId !== undefined
                    ? t(
                        "Payé par {{card}} le {{date}} · s'ajoute à son solde, ne sort pas du compte",
                        {
                          card:
                            step.debts.find(d => d.id === charge.cardId)
                              ?.name ?? t('la carte'),
                          date: textDate(charge.date),
                        },
                      )
                    : charge.refusedBy !== undefined
                      ? t(
                          'Refusé par {{card}} : plus de place, sort du compte le {{date}}',
                          {
                            card:
                              step.debts.find(d => d.id === charge.refusedBy)
                                ?.name ?? t('la carte'),
                            date: textDate(charge.date),
                          },
                        )
                      : charge.recurring === false
                        ? t('Charge ponctuelle · à payer le {{date}}', {
                            date: longDate(charge.date),
                          })
                        : t('à payer le {{date}}', {
                            date: longDate(charge.date),
                          })
              }
              detailColor={
                charge.skipped || charge.refusedBy !== undefined
                  ? theme.warningText
                  : charge.recurring === false
                    ? theme.mobileNavItemSelected
                    : undefined
              }
              note={charge.skipped ? skippedNote(charge) : undefined}
              value={money(-charge.amount)}
              muted={charge.skipped}
              struck={charge.skipped}
              action={
                <PaySwitch
                  label={t('Payer : {{name}}', { name: charge.name })}
                  isOn={!charge.skipped}
                  onChange={isPaid => setPaid(charge, isPaid)}
                />
              }
            />
          ))}
          <View style={{ padding: '12px 16px' }}>
            <Button
              onPress={() =>
                navigate(`/previsions?ajouter=depense&date=${step.payday}`)
              }
              style={{ minHeight: 44 }}
            >
              <Trans>Ajouter une charge ponctuelle</Trans>
            </Button>
          </View>
        </Section>

        <Section
          title={t('Ce que devient le reste')}
          aside={money(step.cashOut + spent)}
        >
          {rest.map((row, n) =>
            isValidElement<{ last?: boolean }>(row)
              ? cloneElement(row, { last: n === rest.length - 1 })
              : row,
          )}
        </Section>

        <Section
          title={t('Contrôles du calcul')}
          aside={
            <Text
              style={{
                fontWeight: 600,
                color:
                  passed === checks.length
                    ? theme.noticeTextLight
                    : theme.warningText,
              }}
            >
              {t('{{passed}} sur {{total}}', {
                passed,
                total: checks.length,
              })}
            </Text>
          }
        >
          {checks.map((check, n) => (
            <Line
              key={check.name}
              last={n === checks.length - 1}
              title={
                <Text style={{ fontSize: 13, fontWeight: 400 }}>
                  {check.name}
                </Text>
              }
              detail={check.ok ? undefined : check.detail}
              detailColor={theme.warningText}
              value={check.ok ? t('OK') : t('À voir')}
              valueColor={check.ok ? theme.noticeTextLight : theme.warningText}
            />
          ))}
        </Section>
      </View>
    </MtxPage>
  );
}
